import * as Sentry from "@sentry/react";

import { put, retry, select, takeEvery, takeLatest } from "redux-saga/effects";

import { AnswerType } from "./types/BaseQuestionResource";
import { AxiosResponse } from "axios";
import { BASE_API_URL } from "../../config";
import { LinkTitle } from "./types/Link";
import { ReduxState } from "../rootReducer";
import { actions } from "./slice";
import { createRequest } from "../../utils/requestUtils";
import { replaceUrlSlashAndDot } from "./utils";
import { sanitizeString } from "../../utils/sentryUtils";

export function* startInterview({
  payload,
}: ReturnType<typeof actions.requestStartInterview>) {
  const { caseId } = payload;
  try {
    const { data }: AxiosResponse = yield retry(5, 1500, createRequest, {
      method: "post",
      url: `${BASE_API_URL}/cases/${caseId}/interview/start`,
    });

    const interview = { ...data, caseId };

    yield put(actions.requestStartInterviewSuccessful(interview));
  } catch (error) {
    Sentry.setContext("saga", {
      name: "Interview",
      function: "startInterview",
    });
    Sentry.captureEvent({
      message: "Unable to start interview",
    });
    Sentry.captureException(error);

    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestStartInterviewFailed(errorMessage));
  }
}

export function* fetchInterview({
  payload,
}: ReturnType<typeof actions.requestInterview>) {
  const { caseId } = payload;
  try {
    const { data }: AxiosResponse = yield retry(10, 1500, createRequest, {
      url: `${BASE_API_URL}/cases/${caseId}/interview`,
    });

    const interview = { ...data, caseId };

    yield put(actions.requestInterviewSuccessful(interview));
  } catch (error) {
    Sentry.setContext("saga", {
      name: "Interview",
      function: "fetchInterview",
    });
    Sentry.captureEvent({
      message: "Unable to fetch interview",
    });
    Sentry.captureException(error);

    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestInterviewFailed(errorMessage));
  }
}

export function* submitBaseQuestion({
  payload,
}: ReturnType<typeof actions.requestSubmitBaseQuestion>) {
  const { _links, body, code } = payload;

  const url = _links.find(
    (link) => link.title === LinkTitle.ANSWER_BASE_QUESTION,
  )?.href;

  try {
    const { data }: AxiosResponse = yield retry(6, 1000, createRequest, {
      method: "patch",
      url: `${url}`,
      requestBody: body,
    });

    yield put(actions.requestSubmitBaseQuestionSuccessful(data));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("questionType", "baseQuestion");
    Sentry.setTag("questionCode", code);
    Sentry.setTag("answerType", body.answerType);
    if (_links[0].href) {
      Sentry.setTag("apiUrl", sanitizeString(_links[0].href));
    }
    Sentry.setContext("saga", {
      name: "Interview",
      function: "submitBaseQuestion",
      title: _links[0].title,
      questionCode: code,
      answerType: body.answerType,
      href: _links[0].href ? sanitizeString(_links[0].href) : "",
    });
    Sentry.captureEvent({
      message: "Unable to submit answer to base question",
    });

    const isPicklistError = body.answerType === AnswerType.PICKLIST;

    yield put(
      actions.requestSubmitBaseQuestionFailed({
        code: code,
        errorMessage: "Kunne ikke registrere svaret ditt. Prøv igjen.",
        picklistError: isPicklistError,
      }),
    );
  }
}

function* submitDisclosure({
  payload,
}: ReturnType<typeof actions.requestSubmitDisclosure>) {
  const { _links, questions } = payload;

  let sanitizedUrl: string | undefined = undefined;
  try {
    const url = _links.find(
      (link) => link.title === LinkTitle.ANSWER_REFLEXIVE_QUESTION,
    )?.href;

    // Encode special characters in disclosure name according to MunichRE rules.
    const splitUrl = url?.split("/disclosures/");
    if (splitUrl && splitUrl[1]) {
      splitUrl[1] = replaceUrlSlashAndDot(splitUrl[1]);
    }

    // Rebuild sanitized url.
    sanitizedUrl = splitUrl?.join("/disclosures/");

    const { data }: AxiosResponse = yield retry(6, 1000, createRequest, {
      method: "patch",
      url: `${sanitizedUrl}?expand=true&showResults=true`,
      requestBody: { questions: questions },
    });

    yield put(actions.requestSubmitDisclosureSuccessful(data));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    questions.forEach((question) =>
      Sentry.setTag("answerType", question.answerType),
    );
    _links.forEach((link) =>
      Sentry.setTag("apiUrl", link.href ? sanitizeString(link.href) : ""),
    );
    Sentry.setTag("aliasName", payload.aliasName);
    Sentry.setTag("questionType", "disclosure");
    Sentry.setContext("saga", {
      name: "Interview",
      function: "submitDisclosure",
      title: _links[0].title,
      aliasName: payload.aliasName,
      href: sanitizedUrl
        ? sanitizeString(`${sanitizedUrl}?expand=true&showResults=true`)
        : "",
    });
    Sentry.captureEvent({
      message: "Unable to submit answer to disclosure",
    });

    yield put(
      actions.requestSubmitDisclosureFailed({
        aliasName: payload.aliasName,
        error: {
          errorIndex: payload.submittedDisclosureIndex,
          errorMessage: "Kunne ikke registrere svaret ditt. Prøv igjen",
        },
      }),
    );
  }
}

function* deleteDisclosure({
  payload,
}: ReturnType<typeof actions.requestDeleteDisclosure>) {
  const { baseQuestionCode, disclosureName } = payload;

  const { caseId } = yield select(
    (state: ReduxState) => state.interview.questions,
  );

  const url = `${BASE_API_URL}/cases/${caseId}/questions/${baseQuestionCode}/disclosures/${replaceUrlSlashAndDot(
    disclosureName,
  )}`;

  try {
    yield retry(6, 1000, createRequest, {
      method: "delete",
      url: url,
    });

    yield put(
      actions.requestDeleteDisclosureSuccessful({
        disclosureName,
        baseQuestionCode: baseQuestionCode,
      }),
    );
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("baseQuestionCode", baseQuestionCode);
    Sentry.setTag("disclosureName", disclosureName);
    Sentry.setTag("apiUrl", sanitizeString(url));
    Sentry.setContext("saga", {
      name: "Interview",
      function: "deleteDisclosure",
      disclosureName: disclosureName,
      baseQuestionCode: baseQuestionCode,
      href: sanitizeString(url),
    });
    Sentry.captureEvent({
      message: "Unable to delete disclosure",
    });

    yield put(
      actions.requestDeleteDisclosureFailed({
        code: baseQuestionCode,
        errorMessage: "Kunne ikke registrere svaret ditt. Prøv igjen.",
        picklistError: true,
      }),
    );
  }
}

function* interviewSaga() {
  yield takeLatest(actions.requestInterview, fetchInterview);
  yield takeEvery(actions.requestStartInterview, startInterview);
  yield takeEvery(actions.requestSubmitBaseQuestion, submitBaseQuestion);
  yield takeEvery(actions.requestSubmitDisclosure, submitDisclosure);
  yield takeEvery(actions.requestDeleteDisclosure, deleteDisclosure);
}

export default interviewSaga;
